import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  ChevronDown,
  Cog,
  Headphones,
  ShieldCheck,
} from "lucide-react";
import PicImg from "./PicImg";

/* =====================================================================
   HeroCinematic — LIFTILE homepage hero.
   Single uploaded photograph (Multi Functional Tower Hoist at sunset)
   as full-screen background. All text/CTAs/feature-bar/particles are
   HTML/SVG layers above. Absolutely no additional imagery generated.
   ===================================================================== */

const HERO_IMAGE = "/hero-liftile-sunset.png";

/* Bottom feature bar — corporate credential row shown at the bottom
   edge of the hero, matching the pattern used by Caterpillar / Volvo
   CE / JCB. Renders full-width, always visible above the fold. */
const FEATURE_BAR = [
  { Icon: Award, label: "Premium Quality" },
  { Icon: Cog, label: "Advanced Engineering" },
  { Icon: Headphones, label: "Nationwide Support" },
  { Icon: ShieldCheck, label: "Warranty" },
];

/* --------------------------------------------------------------------
   Floating dust particles — soft warm/cream, very low opacity, drift
   upward extremely slowly.
   -------------------------------------------------------------------- */
function DustParticles({ count = 32 }) {
  const [particles] = useState(() =>
    Array.from({ length: count }).map(() => ({
      size: 1 + Math.random() * 2.4,
      left: Math.random() * 100,
      delay: -Math.random() * 22,
      duration: 18 + Math.random() * 14, // slow — 18–32s
      opacity: 0.18 + Math.random() * 0.35,
      drift: -30 + Math.random() * 60,
      sway: 6 + Math.random() * 14,
      warm: Math.random() > 0.55,
    }))
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          className="absolute block rounded-full"
          style={{
            left: `${p.left}%`,
            bottom: "-12px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.warm ? "#F2D9B4" : "#F5EFE6",
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 3}px ${
              p.warm ? "rgba(242,217,180,0.5)" : "rgba(245,239,230,0.4)"
            }`,
            animation: `hc-dust ${p.duration}s linear ${p.delay}s infinite`,
            ["--hc-drift"]: `${p.drift}px`,
            ["--hc-sway"]: `${p.sway}px`,
            willChange: "transform, opacity",
          }}
        />
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------
   Tiny orange glowing particles — very few, very slow, warm accent
   sparks near the mast.
   -------------------------------------------------------------------- */
function OrangeSparks({ count = 10 }) {
  const [sparks] = useState(() =>
    Array.from({ length: count }).map(() => ({
      size: 1.4 + Math.random() * 1.8,
      left: 45 + Math.random() * 40, // clustered around the mast side
      delay: -Math.random() * 18,
      duration: 14 + Math.random() * 10,
      opacity: 0.25 + Math.random() * 0.35,
    }))
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]" aria-hidden>
      {sparks.map((s, i) => (
        <span
          key={i}
          className="absolute block rounded-full"
          style={{
            left: `${s.left}%`,
            bottom: "-8px",
            width: `${s.size}px`,
            height: `${s.size}px`,
            background: "#F97316",
            opacity: s.opacity,
            boxShadow: "0 0 10px rgba(249,115,22,0.85), 0 0 20px rgba(249,115,22,0.5)",
            animation: `hc-spark ${s.duration}s ease-in-out ${s.delay}s infinite`,
            willChange: "transform, opacity",
          }}
        />
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------
   Orange energy line — subtle vertical glow slowly travelling up the
   mast area (positioned ~63% from the left where the tower mast sits
   in the uploaded photograph).
   -------------------------------------------------------------------- */
function MastEnergyLine() {
  return (
    <div
      aria-hidden
      className="hidden md:block absolute inset-y-0 pointer-events-none z-[2]"
      style={{ left: "63%", width: "3px" }}
    >
      <span
        className="absolute left-1/2 -translate-x-1/2 w-[2px] h-[35%] rounded-full"
        style={{
          background:
            "linear-gradient(to top, rgba(249,115,22,0) 0%, rgba(249,115,22,0.9) 55%, rgba(255,180,120,1) 90%, rgba(255,255,255,0.9) 100%)",
          filter: "blur(1px)",
          opacity: 0.25,
          boxShadow: "0 0 22px rgba(249,115,22,0.65)",
          animation: "hc-energy 5s ease-in-out infinite",
          willChange: "transform, opacity",
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------------
   Sunlight rays — very soft warm rays fanning from the upper-left
   sunset area of the photograph.
   -------------------------------------------------------------------- */
function SunlightRays() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none z-[1] mix-blend-screen"
      style={{
        background:
          "radial-gradient(60% 60% at 12% 18%, rgba(255,180,90,0.20) 0%, rgba(255,180,90,0.08) 40%, transparent 70%)",
      }}
    />
  );
}

export default function HeroCinematic() {
  const sectionRef = useRef(null);

  /* Very subtle mouse parallax */
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const onMouseMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    setMouse({
      x: (e.clientX - r.left) / r.width - 0.5,
      y: (e.clientY - r.top) / r.height - 0.5,
    });
  };

  const scrollToProducts = () => {
    const el =
      document.getElementById("home-product-categories") ||
      document.querySelector("[data-testid='home-product-categories']");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    }
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#0B0B0B]"
      style={{ minHeight: "100vh", height: "100vh" }}
    >
      {/* ─── LAYER 1: Background photograph ─── */}
      <div
        className="absolute inset-0 z-0"
        style={{
          transform: `translate3d(${mouse.x * -5}px, ${mouse.y * -5}px, 0)`,
          animation: "hc-machine-float 6s ease-in-out infinite",
          willChange: "transform",
        }}
      >
        <PicImg
          src={HERO_IMAGE}
          alt="LIFTILE Multi Functional Tower Hoist at sunset on a high-rise construction site in India"
          loading="eager"
          fetchPriority="high"
          width={1920}
          height={1280}
          className="w-full h-full object-cover select-none"
          style={{
            objectPosition: "center 38%",
            filter: "brightness(1.22) contrast(1.08)",
          }}
          draggable={false}
        />
      </div>

      {/* ─── LAYER 2: Dark black overlay for text legibility ─── */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] bg-[#0B0B0B]"
        style={{ opacity: 0.42 }}
      />
      {/* Left-side dark gradient (0% → 45% opacity) — behind the text column
          for premium readability, per spec */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.28) 35%, rgba(0,0,0,0.08) 65%, rgba(0,0,0,0) 100%)",
        }}
      />
      {/* Bottom vignette to blend into the next section */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 z-[1] h-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.35) 55%, transparent 100%)",
        }}
      />

      {/* ─── LAYER 3: Soft sunlight rays ─── */}
      <SunlightRays />

      {/* ─── LAYER 4: Floating particles & orange sparks ─── */}
      <DustParticles count={30} />
      <OrangeSparks count={9} />

      {/* ─── LAYER 5: Mast energy line ─── */}
      <MastEnergyLine />

      {/* ─── LAYER 6: Content ─── */}
      <div className="relative z-10 h-full w-full flex items-center pt-32 md:pt-40 lg:pt-44 xl:pt-48 pb-24 md:pb-28 lg:pb-32">
        <div className="container-x w-full">
          <div className="grid lg:grid-cols-[minmax(0,45%)_minmax(0,55%)] gap-8 items-center">
            {/* LEFT COLUMN — all content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              style={{
                transform: `translate3d(${mouse.x * -3}px, ${mouse.y * -3}px, 0)`,
                willChange: "transform",
              }}
              className="max-w-[650px]"
            >
              {/* Eyebrow */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className="inline-flex items-center gap-3 mb-6 md:mb-8"
              >
                <span className="block w-9 h-px bg-liftile-orange" />
                <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.3em] text-liftile-orange font-semibold">
                  Premium Construction Machinery Manufacturer
                </span>
              </motion.div>

              {/* Headline (reduced ~10% from previous scale) */}
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-headline"
                className="font-display uppercase text-white text-[2.95rem] sm:text-[3.4rem] md:text-6xl lg:text-[5.25rem] xl:text-[6rem] leading-[0.9] tracking-tight"
                style={{ letterSpacing: "-0.015em" }}
              >
                <span className="block font-extrabold">Built to Lift.</span>
                <span
                  className="block font-extrabold text-liftile-orange"
                  style={{ color: "#F97316" }}
                >
                  Engineered to Last.
                </span>
              </motion.h1>

              {/* Subtitle — tighter gap */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-subtitle"
                className="font-body text-white/85 mt-[30px] md:mt-[34px] text-base md:text-lg lg:text-xl leading-relaxed max-w-[620px]"
              >
                Reliable Construction Machinery Manufacturer delivering premium
                lifting solutions engineered for performance, safety and long
                service life.
              </motion.p>

              {/* CTA Buttons — tighter gap */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col sm:flex-row flex-wrap gap-4 mt-[40px] md:mt-[48px]"
              >
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="group relative inline-flex items-center justify-center gap-3 rounded-full bg-liftile-orange text-white font-heading font-semibold uppercase tracking-[0.14em] text-xs md:text-sm px-8 py-4 transition-all duration-400 hover:-translate-y-[5px] hover:shadow-[0_22px_60px_-12px_rgba(249,115,22,0.85)]"
                  style={{ backgroundColor: "#F97316" }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-40 blur-xl transition-opacity duration-500"
                    style={{ backgroundColor: "#F97316" }}
                  />
                  <span className="relative">Get a Free Quote</span>
                  <ArrowRight className="relative w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/products"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-transparent border border-white/60 text-white font-heading font-semibold uppercase tracking-[0.14em] text-xs md:text-sm px-8 py-4 transition-all duration-300 hover:bg-white hover:text-black hover:border-white hover:-translate-y-[3px]"
                >
                  Explore Products
                </Link>
              </motion.div>
            </motion.div>

            {/* RIGHT COLUMN — intentionally empty so the photograph's
                machine remains fully visible. Never place UI over the
                machine. */}
            <div aria-hidden className="hidden lg:block" />
          </div>
        </div>
      </div>

      {/* ─── LAYER 6b: Slim credential strip pinned to the bottom of the hero. ─── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.55 }}
        data-testid="hero-feature-bar"
        className="absolute inset-x-0 bottom-0 z-10 pointer-events-none"
      >
        <div
          className="w-full backdrop-blur-md border-t border-b border-white/[0.10]"
          style={{ backgroundColor: "rgba(11,11,11,0.60)" }}
        >
          <div className="container-x">
            <p
              className="font-heading text-white/90 text-center uppercase tracking-[0.14em] md:tracking-[0.22em] text-[11px] sm:text-[12px] md:text-[13px] font-semibold py-3 md:py-3.5 leading-relaxed"
            >
              Trusted by 600+ Customers
              <span className="text-liftile-orange mx-2 md:mx-3">•</span>
              600+ Projects Completed
              <span className="text-liftile-orange mx-2 md:mx-3">•</span>
              Premium Construction Equipment Manufacturer
              <span className="text-liftile-orange mx-2 md:mx-3">•</span>
              Made in India
            </p>
          </div>
        </div>
      </motion.div>

      {/* ─── LAYER 7: Scroll indicator (positioned above the feature bar) ─── */}
      <button
        type="button"
        onClick={scrollToProducts}
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to products section"
        className="group absolute left-1/2 -translate-x-1/2 bottom-[96px] md:bottom-[100px] z-10 flex flex-col items-center gap-2 focus:outline-none"
      >
        <div className="relative w-6 h-10 rounded-full border-2 border-white/70 flex items-start justify-center pt-2 transition-colors duration-300 group-hover:border-liftile-orange">
          <span
            aria-hidden
            className="block w-1.5 h-1.5 rounded-full bg-liftile-orange"
            style={{
              backgroundColor: "#F97316",
              animation: "hc-scroll-dot 1.6s ease-in-out infinite",
              willChange: "transform, opacity",
            }}
          />
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-white/70 group-hover:text-liftile-orange transition-colors duration-300" />
      </button>

      {/* ─── Global keyframes for this hero only ─── */}
      <style>{`
        @keyframes hc-machine-float {
          0%   { transform: translate3d(0, -5px, 0); }
          50%  { transform: translate3d(0,  5px, 0); }
          100% { transform: translate3d(0, -5px, 0); }
        }
        @keyframes hc-dust {
          0%   { transform: translate3d(0, 0, 0); opacity: 0; }
          10%  { opacity: 1; }
          50%  { transform: translate3d(calc(var(--hc-sway) * -0.5), -55vh, 0); }
          92%  { opacity: 0.5; }
          100% { transform: translate3d(var(--hc-drift), -110vh, 0); opacity: 0; }
        }
        @keyframes hc-spark {
          0%   { transform: translate3d(0, 0, 0) scale(0.8); opacity: 0; }
          15%  { opacity: 1; }
          70%  { transform: translate3d(6px, -70vh, 0) scale(1.15); opacity: 0.85; }
          100% { transform: translate3d(-4px, -105vh, 0) scale(0.9); opacity: 0; }
        }
        @keyframes hc-energy {
          0%   { transform: translateY(60vh); opacity: 0; }
          10%  { opacity: 0.25; }
          70%  { opacity: 0.25; }
          100% { transform: translateY(-70vh); opacity: 0; }
        }
        @keyframes hc-scroll-dot {
          0%   { transform: translateY(0); opacity: 0.4; }
          40%  { opacity: 1; }
          100% { transform: translateY(14px); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="hc-machine-float"],
          [style*="hc-dust"],
          [style*="hc-spark"],
          [style*="hc-energy"],
          [style*="hc-scroll-dot"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
